import React from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

/**
 * Props for the DesktopIcon component
 */
interface DesktopIconProps {
    /** The label text displayed below the icon */
    label: string;
    /** The icon element to display */
    icon: React.ReactNode;
    /** Optional navigation path */
    path?: string;
    /** Optional unique identifier */
    id?: string;
    /** Optional click handler */
    onClick?: () => void;
}

/**
 * Desktop icon component representing an application or shortcut.
 * Can navigate to a path or execute a custom onClick handler.
 *
 * @param props - The props for the component
 * @returns The Desktop Icon UI
 */
const DesktopIcon: React.FC<DesktopIconProps> = ({ label, icon, path, id, onClick }) => {
    const navigate = useNavigate();

    const handleClick = () => {
        if (onClick) {
            onClick();
        } else if (path) {
            navigate(path);
        }
    };

    return (
        <Box
            id={id}
            onClick={handleClick}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: { xs: 72, sm: 85, md: 100 },
                cursor: 'pointer',
                p: { xs: 0.5, sm: 1 },
                borderRadius: 1,
                '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(4px)',
                },
                '&:active': {
                    transform: 'scale(0.95)',
                },
                transition: 'all 0.2s',
                touchAction: 'manipulation', // Better touch handling
            }}
        >
            <Box
                sx={{
                    width: { xs: 48, sm: 56, md: 64 },
                    height: { xs: 48, sm: 56, md: 64 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: 2,
                    mb: 1,
                    boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
                    backdropFilter: 'blur(5px)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    color: 'white',
                    '& svg': {
                        fontSize: { xs: 28, sm: 34, md: 40 },
                    }
                }}
            >
                {icon}
            </Box>
            <Typography
                variant="body2"
                sx={{
                    color: 'white',
                    textShadow: '0 1px 3px rgba(0,0,0,0.8)',
                    textAlign: 'center',
                    fontWeight: 500,
                    fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.875rem' },
                    lineHeight: 1.2,
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                }}
            >
                {label}
            </Typography>
        </Box>
    );
};

export default DesktopIcon;
